import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { formatDate } from "../../lib/dateUtils";

const REPORT_TYPE_LABELS = {
  inappropriate_behavior: "Inappropriate Behavior", consent_boundary_issue: "Consent / Boundary Issue",
  harassment: "Harassment", photographer_conduct: "Photographer Conduct", participant_conduct: "Participant Conduct",
  no_show: "No Show", unsafe_location: "Unsafe Location", discrimination: "Discrimination",
  payment_scam_concern: "Payment / Scam Concern", other: "Other",
};
const STATUS_LABELS = { new: "New", reviewing: "Reviewing", awaiting_information: "Awaiting Information", action_required: "Action Required", resolved: "Resolved", closed: "Closed" };
const STATUS_COLORS = {
  new: "bg-red-100 text-red-700", reviewing: "bg-stranger-orange text-clean-white", awaiting_information: "bg-stranger-orange text-clean-white",
  action_required: "bg-red-100 text-red-700", resolved: "bg-midnight-black text-clean-white", closed: "bg-soft-gray text-midnight-black/70",
};
const SEVERITY_COLORS = { low: "bg-soft-gray text-midnight-black/70", medium: "bg-stranger-orange/20 text-stranger-orange", high: "bg-red-100 text-red-700", critical: "bg-red-600 text-clean-white" };

export const SafetyTab = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState(null);
  const [status, setStatus] = useState("");
  const [severity, setSeverity] = useState("");
  const [reportType, setReportType] = useState("");

  useEffect(() => {
    api
      .get("/admin/safety", { params: { status: status || undefined, severity: severity || undefined, report_type: reportType || undefined } })
      .then(({ data }) => setReports(data));
  }, [status, severity, reportType]);

  return (
    <div data-testid="admin-safety-tab">
      <div className="flex flex-wrap items-end gap-3 mb-8">
        <div className="w-52">
          <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Status</label>
          <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="safety-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-44">
          <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Severity</label>
          <Select value={severity || "all"} onValueChange={(v) => setSeverity(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="safety-severity-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Severities</SelectItem>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-56">
          <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Report Type</label>
          <Select value={reportType || "all"} onValueChange={(v) => setReportType(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="safety-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {Object.entries(REPORT_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {reports === null ? (
        <p className="font-medium text-midnight-black/60">Loading reports...</p>
      ) : reports.length === 0 ? (
        <p className="font-medium text-midnight-black/60" data-testid="no-safety-reports">No safety reports match.</p>
      ) : (
        <div className="space-y-3" data-testid="safety-reports-list">
          {reports.map((r) => (
            <div
              key={r.id}
              data-testid={`safety-report-row-${r.id}`}
              onClick={() => navigate(`/admin/safety/${r.id}`)}
              className="cursor-pointer border border-soft-gray rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap hover:border-stranger-orange transition-colors"
            >
              <div>
                <p className="font-extrabold text-sm uppercase">{REPORT_TYPE_LABELS[r.report_type] || r.report_type}</p>
                <p className="text-xs font-medium text-midnight-black/60">
                  Reported by {r.reporter_name || r.reporter_role} {r.session_title && `· ${r.session_title}`} · {formatDate(r.created_at)}
                </p>
                <p className="text-xs text-midnight-black/50 mt-1 max-w-lg truncate">{r.description}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${SEVERITY_COLORS[r.severity]}`} data-testid={`safety-severity-${r.id}`}>
                  {r.severity}
                </span>
                <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full whitespace-nowrap ${STATUS_COLORS[r.status]}`} data-testid={`safety-status-${r.id}`}>
                  {STATUS_LABELS[r.status] || r.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
